import type { JobTypeBreakdown } from '../types'

interface Props {
  data: JobTypeBreakdown[]
}

function fmt(val: number): string {
  return `$${val.toLocaleString('en-US')}`
}

const TYPE_COLORS: Record<string, string> = {
  'Walk-In': 'bg-blue-500',
  'Reach-In': 'bg-purple-500',
  'Cooking': 'bg-orange-500',
  'Ice Machine': 'bg-cyan-500',
}

export function JobTypesBreakdown({ data }: Props) {
  if (data.length === 0) return null
  const maxRevenue = Math.max(...data.map((d) => d.revenue), 1)

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Job Types Breakdown</h3>
      <div className="space-y-3">
        {data.map((jt) => {
          const widthPct = Math.max((jt.revenue / maxRevenue) * 100, 8)
          const barColor = TYPE_COLORS[jt.type] || 'bg-gray-500'
          return (
            <div key={jt.type}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{jt.type}</span>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>{jt.count} jobs ({jt.done} done)</span>
                  <span className="font-medium text-gray-700">{fmt(jt.revenue)}</span>
                  <span className="text-green-600">{fmt(jt.collected)} collected</span>
                </div>
              </div>
              <div className="h-6 bg-gray-100 rounded-lg overflow-hidden flex">
                <div
                  className={`h-full ${barColor} rounded-lg transition-all duration-500 flex items-center pl-2`}
                  style={{ width: `${widthPct}%` }}
                >
                  {widthPct > 15 && (
                    <span className="text-xs font-medium text-white">{fmt(jt.revenue)}</span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">Total</span>
        <div className="flex items-center gap-4">
          <span className="text-gray-500">{data.reduce((s, d) => s + d.count, 0)} jobs</span>
          <span className="font-medium text-gray-700">{fmt(data.reduce((s, d) => s + d.revenue, 0))}</span>
          <span className="text-green-600">{fmt(data.reduce((s, d) => s + d.collected, 0))} collected</span>
        </div>
      </div>
    </div>
  )
}
